// src/pages/QuotePage.jsx
//
// Public book-printing estimator. Built from the owner's design mock; all styling lives in
// styles/Quote.css (scoped under .quote-page) rather than MUI sx, matching Pricing.jsx.
//
// Prices are NOT hardcoded here. Every category, option, price, discount tier and the setup
// fee come from GET /api/quotes/pricing, and the running total comes from
// POST /api/quotes/calculate — so the numbers stay editable by admins in AdminPricingPanel.
// The mock shipped its own price table and a different formula (per-page print/paper
// surcharges); those were deliberately not ported. If a price looks wrong, fix the data.
//
// The mock's coupon box is not here: nothing server-side can validate or apply a coupon
// yet, and a client-side code list would be both visible in devtools and unenforced.
// (The October revision of the mock brought it back; it stays out for the same reason.)
//
// Below the quote, the "Retail pricing & royalties" panel (components/quote/RoyaltyPanel)
// plans what readers pay and how sales are shared. Its state lives here because "Start
// your project" saves it onto the manuscript too, as `sales_plan` beside `print_spec`.
//
// The same page serves the app, at /manuscript/:id/pricing (Library → ⋮ → Book Pricing
// Estimator). There it opens on that manuscript's own settings rather than the defaults,
// sits in the app's Navbar and Footer instead of the marketing ones (the path is not in
// App.js's OWN_NAV_ROUTES), and its button is "Save": a PUT of `print_spec` and
// `sales_plan` back onto that manuscript, which never creates one. Nothing renders, and
// so nothing can be saved, until the manuscript has loaded.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import BookPreview from '../components/quote/BookPreview';
import RoyaltyPanel from '../components/quote/RoyaltyPanel';
import { DEFAULT_SELECTIONS } from '../config/printDefaults';
import { fromQuoteSelection } from '../config/bookSizes';
import { getJSON, putJSON } from '../utils/api';
import { specsForDraft } from '../utils/intake';
import { defaultPlan, fromSalesPlan, toSalesPlan } from '../utils/royalty';
import '../styles/Quote.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

// DEFAULT_SELECTIONS lives in config/printDefaults.js, shared with the print
// intake's book specs. Anything not present in the fetched pricing is dropped below.

// Survives the Cognito round-trip, which is a full page navigation away and back.
const PENDING_KEY = 'rv_quote_pending';

const money = (n) =>
  `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// /api/quotes/calculate returns per-unit component rows AND the two order-level rows
// (volume discount, setup fee) in one breakdown array. The panel already shows those two as
// their own lines, so they're filtered out of the per-book breakdown. Coupled to the labels
// in routes/quotes.py::calculate_quote — keep in sync if those strings change.
const isOrderLevelRow = (label = '') =>
  label.startsWith('Volume Discount') || label.startsWith('Setup Fee');

export default function QuotePage() {
  const navigate = useNavigate();
  const { user, authReady, startCognito } = useAuth();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const { id: draftId } = useParams();
  const inApp = Boolean(draftId);

  const rootRef = useRef(null);
  const [navOpen, setNavOpen] = useState(false);

  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selections, setSelections] = useState(DEFAULT_SELECTIONS);
  const [numDraft, setNumDraft] = useState({}); // in-progress text for the number inputs
  const [quote, setQuote] = useState(null);
  // The selections `quote` was calculated for. Both buttons save the quote's figures beside
  // the selections, so they wait until the two match: clicked inside the debounce, they
  // stored the previous size's price next to the new size.
  const [quotedFor, setQuotedFor] = useState(null);
  const latestSelections = useRef(selections);
  latestSelections.current = selections;
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [salesPlan, setSalesPlan] = useState(defaultPlan);

  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  // In the app: the manuscript, and the selections it opened at. `opened` is what Reset
  // returns to, and what Book size is compared with to decide whether Save moves the
  // trim size.
  const [draft, setDraft] = useState(null);
  const [draftError, setDraftError] = useState(null);
  const [opened, setOpened] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const isOpen = Boolean(opened);

  // ---- pricing ------------------------------------------------------------------------
  const fetchPricing = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/quotes/pricing`);
      if (!res.ok) throw new Error('Failed to fetch pricing');
      const data = await res.json();
      setPricing(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching pricing:', err);
      setError('Unable to load pricing. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPricing(); }, [fetchPricing]);

  // ---- the manuscript (in the app) ----------------------------------------------------
  const fetchDraft = useCallback(async () => {
    if (!draftId) return;
    setDraftError(null);
    try {
      const doc = await getJSON(`/api/drafts/${draftId}`);
      if (!doc) throw new Error('Not signed in');
      setDraft(doc);
    } catch (err) {
      console.error('Error loading manuscript:', err);
      setDraftError("We couldn't load this manuscript. Please try again.");
    }
  }, [draftId]);

  useEffect(() => { fetchDraft(); }, [fetchDraft]);

  // Once both have arrived, open on the manuscript's own settings. Without a saved
  // sales_plan (older manuscripts, or never priced) the panel opens at its defaults.
  useEffect(() => {
    if (!inApp || !pricing || !draft || opened) return;
    const specs = specsForDraft(pricing, draft);
    setSelections(specs);
    setSalesPlan(fromSalesPlan(draft.sales_plan));
    setOpened(specs);
  }, [inApp, pricing, draft, opened]);

  // Drop any default that this pricing document doesn't actually offer, so an admin who
  // renames or removes an option can't leave the page sending a dead id forever.
  useEffect(() => {
    if (!pricing?.categories) return;
    setSelections((prev) => {
      const next = { ...prev };
      let changed = false;
      pricing.categories.forEach((cat) => {
        if (cat.type !== 'select') return;
        const ids = (cat.options || []).map((o) => o.id);
        if (ids.length && !ids.includes(next[cat.id])) {
          next[cat.id] = ids[0];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [pricing]);

  // ---- running quote ------------------------------------------------------------------
  const calculateQuote = useCallback(async () => {
    // In the app, not before the manuscript's selections are in: a quote for the
    // defaults would sit behind Save until the next recalculation.
    if (!pricing || (inApp && !isOpen)) return;
    try {
      const res = await fetch(`${API_BASE}/api/quotes/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ selections }),
      });
      if (!res.ok) throw new Error('Failed to calculate quote');
      const data = await res.json();
      // A slower, older request must not land over a newer one.
      if (latestSelections.current !== selections) return;
      setQuote(data);
      setQuotedFor(selections);
    } catch (err) {
      console.error('Error calculating quote:', err);
    }
  }, [selections, pricing, inApp, isOpen]);

  useEffect(() => {
    const timer = setTimeout(calculateQuote, 300); // debounce the slider drag
    return () => clearTimeout(timer);
  }, [calculateQuote]);

  // ---- scroll reveal ------------------------------------------------------------------
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const els = root.querySelectorAll('.reveal');
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('in'));
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
    // isOpen: in the app the panels first render after the manuscript, not the pricing.
  }, [loading, isOpen]);

  // ---- animated total -----------------------------------------------------------------
  const totalRef = useRef(null);
  const displayedTotal = useRef(null);
  const totalFrame = useRef(null);

  useEffect(() => {
    const el = totalRef.current;
    const target = quote?.total;
    if (!el || target == null) return undefined;

    if (displayedTotal.current === null) {
      displayedTotal.current = target;
      el.textContent = money(target);
      return undefined;
    }

    const start = displayedTotal.current;
    const startTime = performance.now();
    const DURATION = 420;
    cancelAnimationFrame(totalFrame.current);
    el.classList.add('bump');

    const tick = (now) => {
      const p = Math.min(1, (now - startTime) / DURATION);
      const eased = 1 - (1 - p) ** 3;
      el.textContent = money(start + (target - start) * eased);
      if (p < 1) {
        totalFrame.current = requestAnimationFrame(tick);
      } else {
        displayedTotal.current = target;
        el.textContent = money(target);
        el.classList.remove('bump');
      }
    };
    totalFrame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(totalFrame.current);
  }, [quote?.total]);

  // ---- volume-badge pop ---------------------------------------------------------------
  // Only tiers that just became active should animate, so the row doesn't pop on every keystroke.
  const prevActiveTiers = useRef(new Set());
  const [poppingTiers, setPoppingTiers] = useState(new Set());

  useEffect(() => {
    const tiers = pricing?.volume_discounts || [];
    const active = new Set(tiers.filter((d) => selections.quantity >= d.min_qty).map((d) => d.min_qty));
    const newly = new Set([...active].filter((q) => !prevActiveTiers.current.has(q)));
    prevActiveTiers.current = active;
    if (!newly.size) return undefined;
    setPoppingTiers(newly);
    const t = setTimeout(() => setPoppingTiers(new Set()), 500);
    return () => clearTimeout(t);
  }, [selections.quantity, pricing]);

  // ---- selection handlers -------------------------------------------------------------
  const setSelection = (id, value) => setSelections((prev) => ({ ...prev, [id]: value }));

  const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

  const handleNumberText = (cat, text) => {
    setNumDraft((prev) => ({ ...prev, [cat.id]: text }));
    const parsed = parseInt(text, 10);
    // Commit only once the typed value is genuinely in range — otherwise typing "1" on the
    // way to "1000" would clamp to the minimum and fight the user.
    if (!Number.isNaN(parsed) && parsed >= cat.min && parsed <= cat.max) setSelection(cat.id, parsed);
  };

  const commitNumber = (cat) => {
    const draft = numDraft[cat.id];
    if (draft !== undefined) {
      const parsed = parseInt(draft, 10);
      setSelection(cat.id, Number.isNaN(parsed) ? cat.min : clamp(parsed, cat.min, cat.max));
    }
    setNumDraft((prev) => {
      const next = { ...prev };
      delete next[cat.id];
      return next;
    });
  };

  // In the app, back to what the manuscript has saved rather than to the defaults.
  const resetOptions = () => {
    setSelections(inApp && opened ? opened : DEFAULT_SELECTIONS);
    setNumDraft({});
    setCreateError(null);
    setSaveError(null);
  };

  // ---- "Start your project" -----------------------------------------------------------
  // Both halves of the page go onto the draft: the print configuration as `print_spec`,
  // the retail & royalty panel as `sales_plan` (sanitised server-side in routes/drafts.py).
  const createManuscript = useCallback(async (spec, plan) => {
    setCreating(true);
    setCreateError(null);
    try {
      const accessToken = localStorage.getItem('rv_access_token');
      const res = await fetch(`${API_BASE}/api/drafts`, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          title: 'Untitled manuscript',
          print_spec: spec,
          ...(plan ? { sales_plan: toSalesPlan(plan) } : {}),
        }),
      });
      if (!res.ok) throw new Error(`Create draft failed: ${res.status}`);
      const draft = await res.json();
      const draftId = draft.id || draft._id;
      if (!draftId) throw new Error('Draft created but no id returned');
      sessionStorage.removeItem(PENDING_KEY);
      navigate(`/editor/${draftId}`);
    } catch (err) {
      console.error(err);
      setCreateError("We couldn't start your project. Please try again.");
      setCreating(false);
    }
  }, [navigate]);

  const buildSpec = useCallback(() => ({
    selections,
    quantity: quote?.quantity ?? selections.quantity,
    price_per_unit: quote?.price_per_unit ?? null,
    net_price_per_unit: quote?.net_price_per_unit ?? null,
    total: quote?.total ?? null,
    quoted_at: new Date().toISOString(),
  }), [selections, quote]);

  const handleStartProject = () => {
    const spec = buildSpec();
    if (user) {
      createManuscript(spec, salesPlan);
      return;
    }
    // Signed out: park the configuration and send them to sign up. postLoginFrom is what
    // AuthContext's callback handler already reads to decide where to land afterwards.
    // The royalty panel rides along as typed, so a failed create can put it back on screen.
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({ print_spec: spec, sales_plan: salesPlan }));
    sessionStorage.setItem('postLoginFrom', '/quote?resume=1');
    startCognito('signup');
  };

  // ---- "Save" (in the app) ------------------------------------------------------------
  // Writes both halves back onto the manuscript the page opened on; never creates one.
  // Book size is the manuscript's trim size, so changing it here and saving re-lays the
  // book out at the new size, as starting a project from /quote does. Left untouched, the
  // trim size is left alone: a book at a size we don't print (A5, Pocket) opens at a
  // stand-in option, and saving must not quietly turn it into that.
  const newTrimSize =
    inApp && opened && selections.size !== opened.size ? fromQuoteSelection(selections.size) : null;

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    const body = { print_spec: buildSpec(), sales_plan: toSalesPlan(salesPlan) };
    if (newTrimSize) body.trimSize = newTrimSize;
    try {
      const res = await putJSON(`/api/drafts/${draftId}`, body);
      if (!res) throw new Error('Not signed in');
      setOpened(selections);
      if (body.trimSize) setDraft((prev) => ({ ...prev, trimSize: body.trimSize }));
      toast.success('Pricing saved');
    } catch (err) {
      console.error(err);
      setSaveError("We couldn't save your pricing. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Coming back from sign-up: pick the parked configuration back up and create the draft.
  const resumeHandled = useRef(false);
  useEffect(() => {
    if (inApp || resumeHandled.current) return;
    if (searchParams.get('resume') !== '1' || !authReady || !user) return;

    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return;
    resumeHandled.current = true;
    try {
      const parked = JSON.parse(raw);
      // Parked before the royalty panel existed, the entry was the bare print spec.
      const spec = parked?.print_spec || parked;
      const plan = parked?.sales_plan?.parties ? parked.sales_plan : null;
      if (spec?.selections) setSelections((prev) => ({ ...prev, ...spec.selections }));
      if (plan) setSalesPlan(plan);
      createManuscript(spec, plan);
    } catch {
      sessionStorage.removeItem(PENDING_KEY);
    }
  }, [inApp, searchParams, authReady, user, createManuscript]);

  // ---- derived ------------------------------------------------------------------------
  const categories = useMemo(() => pricing?.categories || [], [pricing]);

  const sizeLabel = useMemo(() => {
    const sizeCat = categories.find((c) => c.id === 'size');
    return sizeCat?.options?.find((o) => o.id === selections.size)?.name || '—';
  }, [categories, selections.size]);

  const perBookRows = (quote?.breakdown || []).filter((row) => !isOrderLevelRow(row.item));
  const quoteCurrent = Boolean(quote) && quotedFor === selections;

  // The spec chips under the preview. Option names come from the pricing document, cut at
  // " (" so "Perfect Binding (Hotmelt)" reads "Perfect Binding", as the mock does.
  const specChips = useMemo(() => {
    const optionName = (catId) => {
      const cat = categories.find((c) => c.id === catId);
      const name = cat?.options?.find((o) => o.id === selections[catId])?.name;
      return name ? name.split(' (')[0] : '—';
    };
    return [
      ['Pages', selections.pages ?? '—'],
      ['Paper', optionName('paper_type')],
      ['Print', optionName('print_type')],
      ['Cover', optionName('cover_type')],
      ['Finish', optionName('lamination')],
      ['Binding', optionName('binding')],
    ];
  }, [categories, selections]);

  // Service/Product have no route yet — placeholders, matching Home and Pricing.
  const navItems = [
    { label: 'Service' },
    { label: 'Product' },
    { label: 'About', onClick: () => navigate('/about') },
    { label: 'Pricing', onClick: () => navigate('/pricing') },
  ];

  // ---- render -------------------------------------------------------------------------
  const backToManuscripts = () => navigate('/library', { state: { initialView: 'drafts' } });

  // In the app the global Navbar is already above this, so the marketing nav is left
  // out; the hero stays, named for the manuscript, with a way back to it.
  const masthead = (
    <header className="masthead">
      {!inApp && (
      <div className="wrap">
        <nav>
          <Link to="/" className="site-logo" aria-label="ReVisionari home">
            <img src="/images/revisionari-logo.png" alt="ReVisionari" />
          </Link>
          <div className={`nav-links${navOpen ? ' open' : ''}`} id="quote-nav-links">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.onClick ? '#top' : undefined}
                onClick={item.onClick && ((e) => { e.preventDefault(); setNavOpen(false); item.onClick(); })}
              >
                {item.label}
              </a>
            ))}
            <button type="button" onClick={() => { setNavOpen(false); startCognito('login'); }}>
              Login
            </button>
          </div>
          <button type="button" className="nav-cta" onClick={() => startCognito('signup')}>
            Get started
          </button>
          <button
            type="button"
            className={`nav-toggle${navOpen ? ' open' : ''}`}
            aria-expanded={navOpen}
            aria-controls="quote-nav-links"
            aria-label={navOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setNavOpen((open) => !open)}
          >
            <span />
            <span />
            <span />
          </button>
        </nav>
      </div>
      )}

      <div className="est-hero">
        <div className="glow g1" />
        <div className="glow g2" />
        <div className="wrap">
          {inApp && (
            <button type="button" className="est-back" onClick={backToManuscripts}>
              ← Back to Manuscripts
            </button>
          )}
          <span className="eyebrow">Book pricing</span>
          <h1>
            {inApp
              ? (draft && draft.title) || (draft ? 'Untitled manuscript' : 'Your manuscript')
              : 'Configure your book, get an instant quote'}
          </h1>
          <p>
            Adjust quantity, size, and finish to see live print pricing — including volume
            discounts for larger runs.
          </p>
        </div>
      </div>
    </header>
  );

  const stateBlock = (children) => (
    <div className="quote-page" ref={rootRef}>
      {masthead}
      <div className="state-block">{children}</div>
    </div>
  );

  const draftPending = inApp && !draftError && !opened;
  if (loading || (draftPending && !error)) {
    return stateBlock(
      <>
        <div className="spinner" />
        <p>{inApp ? 'Loading your manuscript…' : 'Loading pricing information…'}</p>
      </>
    );
  }

  // Only the author prices a manuscript: the server refuses anyone else's Save, and a
  // collaborator's Library menu doesn't offer the page. A standalone cover isn't a book.
  const cantPrice =
    inApp && draft && (draft.type === 'cover_only'
      ? 'Pricing is for manuscripts, not standalone covers.'
      : String(draft.author_id) !== String(user?.id)
        ? "Only this manuscript's author can change its pricing."
        : null);

  if (inApp && (draftError || cantPrice)) {
    return stateBlock(
      <>
        <h2>{draftError ? 'Manuscript unavailable' : 'Pricing unavailable'}</h2>
        <p>{draftError || cantPrice}</p>
        {draftError && (
          <button type="button" className="state-retry" onClick={fetchDraft}>
            Try again
          </button>
        )}
        <button type="button" className="state-retry" onClick={backToManuscripts}>
          Back to Manuscripts
        </button>
      </>
    );
  }

  if (error) {
    return (
      <div className="quote-page" ref={rootRef}>
        {masthead}
        <div className="state-block">
          <h2>Pricing is unavailable</h2>
          <p>{error}</p>
          <button type="button" className="state-retry" onClick={fetchPricing}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  const renderField = (cat) => {
    if (cat.type === 'number') {
      const value = selections[cat.id] ?? cat.min;
      const pct = ((value - cat.min) / (cat.max - cat.min)) * 100;
      return (
        <>
          <div className="slider-row">
            <span className="slider-minmax">{cat.min.toLocaleString('en-US')}</span>
            <input
              type="number"
              className="slider-value"
              min={cat.min}
              max={cat.max}
              value={numDraft[cat.id] ?? value}
              onChange={(e) => handleNumberText(cat, e.target.value)}
              onBlur={() => commitNumber(cat)}
              aria-label={cat.name}
            />
            <span className="slider-minmax">{cat.max.toLocaleString('en-US')}</span>
          </div>
          <input
            type="range"
            min={cat.min}
            max={cat.max}
            value={value}
            onChange={(e) => setSelection(cat.id, Number(e.target.value))}
            aria-label={`${cat.name} slider`}
            style={{
              background: `linear-gradient(90deg, var(--purple) 0%, var(--gold) ${pct}%, var(--line-soft) ${pct}%, var(--line-soft) 100%)`,
            }}
          />
        </>
      );
    }

    if (cat.type === 'select') {
      const selected = (cat.options || []).find((o) => o.id === selections[cat.id]);
      const addon = selected?.price ?? 0;
      return (
        <>
          <div className="select-wrap">
            <select
              value={selections[cat.id] ?? ''}
              onChange={(e) => setSelection(cat.id, e.target.value)}
              aria-label={cat.name}
            >
              {(cat.options || []).map((option) => (
                <option key={option.id} value={option.id}>{option.name}</option>
              ))}
            </select>
            <span className="select-arrow" aria-hidden="true">▾</span>
          </div>
          {addon !== 0 && (
            <span className={`addon-tag${addon < 0 ? ' credit' : ''}`}>
              {addon > 0 ? `+${money(addon)}` : `−${money(Math.abs(addon))}`}
            </span>
          )}
          {cat.id === 'size' && newTrimSize && (
            <div className="field-note">
              Saving changes this manuscript&apos;s page size to match, which re-flows its pages.
            </div>
          )}
        </>
      );
    }

    return null;
  };

  return (
    <div className="quote-page" ref={rootRef}>
      {masthead}

      <section className="estimator">
        <div className="wrap est-grid">
          {/* ============ CONFIGURE ============ */}
          <div className="panel reveal config-col">
            <div className="panel-head">
              <div className="panel-badge" aria-hidden="true">📕</div>
              <div className="panel-title">Configure your book</div>
            </div>
            <p className="panel-sub">Adjust the options below to get your custom quote.</p>

            <div className="field-grid">
              {categories.map((cat) => (
                <div className="field" key={cat.id}>
                  <div className="field-label">
                    <span className="field-icon" aria-hidden="true">{cat.icon}</span>
                    {cat.name}
                  </div>
                  <div className="field-desc">{cat.description}</div>
                  {renderField(cat)}
                </div>
              ))}
            </div>
          </div>

          {/* ============ PREVIEW + QUOTE, THEN ROYALTIES ============ */}
          <div className="main-col">
            <div className="pq-row">
              <BookPreview
                selections={selections}
                pricePerUnit={quote?.price_per_unit}
                sizeLabel={sizeLabel}
                chips={specChips}
              />

              <div className="quote-panel reveal">
                <div className="quote-head">
                  <div className="panel-badge" aria-hidden="true">🧮</div>
                  <div className="panel-title">Your quote</div>
                </div>

                <div className="quote-total">
                  <div className="label">Total estimate</div>
                  <div className="amount" ref={totalRef}>
                    {quote ? money(quote.total) : '—'}
                  </div>
                  <div className="sub">
                    {quote
                      ? `For ${quote.quantity.toLocaleString('en-US')} ${quote.quantity === 1 ? 'copy' : 'copies'}`
                      : 'Calculating…'}
                  </div>
                </div>

                <div className="quote-lines">
                  <div className="qline">
                    <span className="lbl">Price per book</span>
                    <span className="val">{quote ? money(quote.price_per_unit) : '—'}</span>
                  </div>

                  <div className="qline">
                    <span className="lbl">Subtotal</span>
                    <span className="val">{quote ? money(quote.subtotal) : '—'}</span>
                  </div>

                  {quote?.discount_percent > 0 && (
                    <div className="qline discount">
                      <span className="lbl">Volume discount ({quote.discount_percent}%)</span>
                      <span className="val">−{money(quote.discount_amount)}</span>
                    </div>
                  )}

                  <div className="qline">
                    <span className="lbl">Setup fee</span>
                    <span className="val">{quote ? money(quote.setup_fee) : '—'}</span>
                  </div>

                  <div className="qline total-line">
                    <span className="lbl">Total</span>
                    <span className="val">{quote ? money(quote.total) : '—'}</span>
                  </div>
                </div>

                {pricing?.volume_discounts?.length > 0 && (
                  <div className="discounts-box">
                    <div className="dt-label">Volume discounts available</div>
                    <div className="dt-badges">
                      {pricing.volume_discounts.map((d) => {
                        const active = selections.quantity >= d.min_qty;
                        return (
                          <span
                            key={d.min_qty}
                            className={`dt-badge${active ? ' active' : ''}${poppingTiers.has(d.min_qty) ? ' pop' : ''}`}
                          >
                            {d.min_qty}+: {d.discount_percent}% off
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  className="breakdown-toggle"
                  onClick={() => setShowBreakdown((open) => !open)}
                  aria-expanded={showBreakdown}
                  aria-controls="quote-breakdown"
                >
                  {showBreakdown ? 'Hide price breakdown' : 'Show price breakdown'}
                </button>
                {/* Rows come straight from the calculate response, so they always describe
                    the selections that produced the number above them. */}
                {showBreakdown && (
                  <div className="breakdown-detail" id="quote-breakdown">
                    {perBookRows.length === 0 && (
                      <div className="bd-row"><span>No breakdown available</span></div>
                    )}
                    {perBookRows.map((row, i) => (
                      <div className={`bd-row${row.price < 0 ? ' credit' : ''}`} key={`${row.item}-${i}`}>
                        <span>{row.item}</span>
                        <b>{row.price < 0 ? `−${money(Math.abs(row.price))}` : money(row.price)}</b>
                      </div>
                    ))}
                  </div>
                )}

                {(createError || saveError) && <div className="quote-error">{createError || saveError}</div>}

                <div className="quote-actions">
                  {inApp ? (
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={handleSave}
                      disabled={saving || !quoteCurrent}
                    >
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={handleStartProject}
                      disabled={creating || !quoteCurrent}
                    >
                      {creating ? 'Setting up your manuscript…' : 'Start your project'}
                    </button>
                  )}
                  <button type="button" className="btn-secondary" onClick={resetOptions} disabled={creating || saving}>
                    Reset options
                  </button>
                </div>

                <div className="quote-fine">
                  Estimate only · final quote confirmed at checkout
                </div>
              </div>
            </div>

            <RoyaltyPanel plan={salesPlan} onChange={setSalesPlan} quote={quote} />
          </div>
        </div>
      </section>
    </div>
  );
}
